import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiShare2,
  FiInfo,
  FiLink,
} from "react-icons/fi";
import { FaWhatsapp, FaTelegram, FaFacebookF } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

import MainContainer from "../../../components/utils/MainContainer";
import Seo, { seoExcerpt } from "../../../components/utils/Seo";
import { ArticleSkeleton } from "../../../components/utils/Skeleton";
import NewsCard from "../../../components/portal/NewsCard";
import NewsArticleBody from "../../../components/portal/NewsArticleBody";
import newsService from "../../../api/services/news";
import useReveal from "../../../hooks/useReveal";

import { getNewsPublicDetails } from "../../../store/slices/news/asyncThunks";
import {
  selectPublicNewsDetails,
  clearPublicNewsDetails,
} from "../../../store/slices/news/slice";

import {
  Container,
  ArticleHeader,
  ArticleCategory,
  ArticleTitle,
  ArticleMeta,
  MetaItem,
  BandTag,
  Breadcrumbs,
  ArticleMain,
  ArticleSidebar,
  SidebarCard,
  SidebarTitle,
  SocialShare,
  ShareButton,
  BackLink,
  RelatedArticles,
  RelatedTitle,
  RelatedGrid,
} from "./NewsDetailsPage.styles";
import {
  formatDateNoHours,
  formatNewsDetailsDate,
  getNewsTypeIcon,
  getNewsTypeLabel,
} from "../../../utils/utils";
import { toast } from "../../../components/modern-ui/sonner";

export default function NewsDetailsPage() {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data: newsResponse,
    loading,
    error,
  } = useSelector(selectPublicNewsDetails);

  const newsData = newsResponse || null;

  // Povezane novosti (v2 s.10.6), ista kategorija ili isti bend
  const [related, setRelated] = useState([]);

  // Scroll-reveal za sekcije ispod članka
  const [merchRef, merchVisible] = useReveal();
  const [relatedRef, relatedVisible] = useReveal();

  useEffect(() => {
    if (newsId) {
      dispatch(clearPublicNewsDetails());
      dispatch(getNewsPublicDetails(newsId));
    }

    return () => {
      dispatch(clearPublicNewsDetails());
    };
  }, [dispatch, newsId]);

  useEffect(() => {
    if (!newsData?.id) return undefined;
    let active = true;

    const loadRelated = async () => {
      try {
        const [page1, page2] = await Promise.all([
          newsService.getNews({ page: 1 }),
          newsService.getNews({ page: 2 }).catch(() => null),
        ]);
        if (!active) return;
        const all = [...(page1?.data || []), ...(page2?.data || [])].filter(
          (item) => item.id !== newsData.id,
        );
        const sameBand = newsData.bandId
          ? all.filter(
              (item) => String(item.bandId) === String(newsData.bandId),
            )
          : [];
        const sameType = all.filter(
          (item) =>
            item.type === newsData.type &&
            !sameBand.some((b) => b.id === item.id),
        );
        setRelated([...sameBand, ...sameType].slice(0, 3));
      } catch {
        if (active) setRelated([]);
      }
    };

    loadRelated();
    return () => {
      active = false;
    };
  }, [newsData?.id, newsData?.bandId, newsData?.type]);

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const handleBack = () => {
    navigate(-1);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = newsData?.title || "Muzičke novosti";

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url,
      )}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text,
      )}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(
        url,
      )}&text=${encodeURIComponent(text)}`,
    };

    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      toast.success("Link kopiran");
      return;
    }
    window.open(shareUrls[platform], "_blank", "width=600,height=400");
  };

  if (loading) {
    return (
      <MainContainer>
        <Container>
          <ArticleSkeleton />
        </Container>
      </MainContainer>
    );
  }

  if (error || !newsData) {
    return (
      <MainContainer>
        <Container>
          <BackLink onClick={handleBack}>
            <FiArrowLeft /> Nazad
          </BackLink>
          <div
            style={{
              textAlign: "center",
              padding: "4rem",
              color: "var(--text-secondary)",
            }}
          >
            <h2>Greška pri učitavanju novosti</h2>
            <p>{error || "Novost nije pronađena"}</p>
          </div>
        </Container>
      </MainContainer>
    );
  }

  const TypeIcon = getNewsTypeIcon(newsData.type);
  const ownerName = newsData.band?.name || newsData.festival?.name || "";
  const ownerPath = newsData.band?.id
    ? `/bend/${newsData.band.slug || newsData.band.id}`
    : newsData.festival?.id
      ? `/festival/${newsData.festival.slug || newsData.festival.id}`
      : null;

  return (
    <MainContainer>
      {/* SEO, meta iz podataka novosti (renderuje se tek kad je učitana) */}
      <Seo
        title={newsData.title}
        description={seoExcerpt(newsData.content || newsData.text)}
        canonical={`/novost/${newsData.slug || newsData.id}`}
        image={newsData.mainImage}
        type="article"
      />
      <Container>
        {/* Breadcrumb (desktop) / Nazad (mobilno), v2 s.10.1 */}
        <Breadcrumbs className="top">
          <Link to="/novosti">Novosti</Link>
          <span className="separator">/</span>
          <span>{getNewsTypeLabel(newsData.type)}</span>
          <span className="separator">/</span>
          <span className="current">{newsData.title}</span>
        </Breadcrumbs>
        <BackLink className="mobile-only" onClick={handleBack}>
          <FiArrowLeft /> Nazad
        </BackLink>

        {/* Hero, slika vidljiva, gradijent samo pri dnu */}
        <ArticleHeader $backgroundImage={newsData.mainImage}>
          <ArticleCategory>
            <TypeIcon size={13} />
            {getNewsTypeLabel(newsData.type)}
          </ArticleCategory>

          <ArticleTitle>{newsData.title}</ArticleTitle>

          <ArticleMeta>
            {ownerName && (
              <BandTag
                as="button"
                type="button"
                style={{ cursor: ownerPath ? "pointer" : "default" }}
                onClick={() => ownerPath && goTo(ownerPath)}
              >
                {ownerName}
              </BandTag>
            )}

            {newsData.date && (
              <MetaItem>
                <FiCalendar size={13} />
                <span>{formatNewsDetailsDate(newsData.date)}</span>
              </MetaItem>
            )}

            {newsData.time && (
              <MetaItem>
                <FiClock size={13} />
                <span>{newsData.time.slice(0, 5)}</span>
              </MetaItem>
            )}

            {newsData.location && newsData.venue && (
              <MetaItem>
                <FiMapPin size={13} />
                <span>
                  {newsData.venue}, {newsData.location}
                </span>
              </MetaItem>
            )}
          </ArticleMeta>
        </ArticleHeader>

        {/* Main Content, deljeno telo novosti (isto se koristi u admin pregledu) */}
        <ArticleMain>
          <NewsArticleBody
            news={newsData}
            onTagClick={(tag) => goTo(`/novosti?q=${encodeURIComponent(tag)}`)}
          />

          {/* Sidebar, jedna Informacije kartica + Podeli (v2 s.10.2) */}
          <ArticleSidebar>
            <SidebarCard>
              <SidebarTitle>
                <FiShare2 />
                Podeli novost
              </SidebarTitle>
              <SocialShare>
                <ShareButton
                  onClick={() => handleShare("facebook")}
                  title="Podeli na Facebook"
                  aria-label="Facebook"
                >
                  <FaFacebookF size={15} />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("twitter")}
                  title="Podeli na X"
                  aria-label="X"
                >
                  <FaXTwitter size={15} />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("whatsapp")}
                  title="Podeli na WhatsApp"
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp size={15} />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("telegram")}
                  title="Podeli na Telegram"
                  aria-label="Telegram"
                >
                  <FaTelegram size={15} />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("copy")}
                  title="Kopiraj link"
                  aria-label="Kopiraj link"
                >
                  <FiLink size={15} />
                </ShareButton>
              </SocialShare>
            </SidebarCard>

            <SidebarCard>
              <SidebarTitle>
                <FiInfo />
                Informacije
              </SidebarTitle>
              <InfoList>
                <div>
                  <dt>Tip</dt>
                  <dd>{getNewsTypeLabel(newsData.type)}</dd>
                </div>
                {newsData.createdAt && (
                  <div>
                    <dt>Objavljeno</dt>
                    <dd>{formatDateNoHours(newsData.createdAt)}</dd>
                  </div>
                )}
                {newsData.date && (
                  <div>
                    <dt>Datum događaja</dt>
                    <dd>{formatDateNoHours(newsData.date)}</dd>
                  </div>
                )}
                {(newsData.venue || newsData.location) && (
                  <div>
                    <dt>Lokacija</dt>
                    <dd>
                      {[newsData.venue, newsData.location]
                        .filter(Boolean)
                        .join(", ")}
                    </dd>
                  </div>
                )}
                {ownerName && (
                  <div>
                    <dt>{newsData.band ? "Bend" : "Organizator"}</dt>
                    <dd>
                      {ownerPath ? (
                        <button
                          type="button"
                          className="owner-link"
                          onClick={() => goTo(ownerPath)}
                        >
                          {ownerName}
                        </button>
                      ) : (
                        ownerName
                      )}
                    </dd>
                  </div>
                )}
              </InfoList>
            </SidebarCard>
          </ArticleSidebar>
        </ArticleMain>

        {/* Most ka prodaji (v2 s.10.6) */}
        {newsData.band?.id && (
          <MerchBanner
            ref={merchRef}
            $visible={merchVisible}
            type="button"
            onClick={() => goTo(ownerPath)}
          >
            Pogledaj merch benda {newsData.band.name}
            <FiArrowRight size={16} />
          </MerchBanner>
        )}

        {/* Još novosti */}
        {related.length > 0 && (
          <RelatedArticles ref={relatedRef} $visible={relatedVisible}>
            <RelatedTitle>Još novosti</RelatedTitle>
            <RelatedGrid>
              {related.map((item) => (
                <NewsCard key={item.id} news={item} />
              ))}
            </RelatedGrid>
          </RelatedArticles>
        )}
      </Container>
    </MainContainer>
  );
}

/* Lokalni stilovi */

const InfoList = styled.dl`
  margin: 0;

  div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border);

    &:last-child {
      border-bottom: none;
    }
  }

  dt {
    color: var(--text-muted);
    font-size: 0.8rem;
  }

  dd {
    margin: 0;
    color: var(--text-primary);
    font-size: 0.88rem;
    text-align: right;

    .owner-link {
      background: none;
      border: none;
      padding: 0;
      color: var(--brand);
      font-size: 0.88rem;
      font-weight: 600;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const MerchBanner = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  height: 52px;
  margin-bottom: 1rem;

  /* reveal na scroll */
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? "0" : "20px")});
  transition:
    opacity 0.5s ease,
    transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1),
    border-color 0.18s ease,
    color 0.18s ease;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
  }
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  svg {
    transition: transform 0.18s ease;
  }

  &:hover {
    border-color: var(--brand);
    color: var(--brand);

    svg {
      transform: translateX(4px);
    }
  }
`;
